"use client";

import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import { useAppSelector } from "@repo/store/hooks";
import { selectIsCheckIn, selectIsPostBooking, useManageBooking } from "@repo/store/slices";

type Props = {
  children: ReactNode;
};

const ExtrasContext = createContext<{
  isOutboundClose: boolean;
  isInboundClose: boolean;
}>({
  isOutboundClose: false,
  isInboundClose: false,
});

export const useExtrasContext = () => {
  const context = useContext(ExtrasContext);
  return context;
};

export default function ExtrasProvider({ children }: Readonly<Props>) {
  const bookingData = useManageBooking();
  const isCheckIn = useAppSelector(selectIsCheckIn);
  const isPostBooking = useAppSelector(selectIsPostBooking);

  const outBoundJourney = bookingData?.journeys?.[0];

  const isOutboundClose: boolean = Boolean(
    (isCheckIn || isPostBooking) &&
    (outBoundJourney?.checkInStatus?.isTooLate ||
      outBoundJourney?.checkInStatus?.isImminent ||
      outBoundJourney?.checkInStatus?.isClosed ||
      (outBoundJourney?.checkInStatus?.isDisabled && !outBoundJourney?.checkInStatus?.isTooEarly)),
  );

  const inBoundJourney =
    (bookingData?.journeys?.length ?? 0) > 1 ? bookingData?.journeys?.[1] : null;

  const isInboundClose: boolean = Boolean(
    (isCheckIn || isPostBooking) &&
    (inBoundJourney?.checkInStatus?.isTooLate ||
      inBoundJourney?.checkInStatus?.isImminent ||
      inBoundJourney?.checkInStatus?.isClosed),
  );

  const value = useMemo(
    () => ({
      isOutboundClose,
      isInboundClose,
    }),
    [isOutboundClose, isInboundClose],
  );

  return <ExtrasContext.Provider value={value}>{children}</ExtrasContext.Provider>;
}
